import React, { useMemo, useState } from "react";

const STAGES = [
  {
    id: "traffic",
    label: "Traffic",
    color: "#00F0FF",
    desc: "Multi-channel acquisition",
  },
  {
    id: "lead",
    label: "Lead",
    color: "#7DD3FC",
    desc: "Identified intent",
  },
  {
    id: "ai",
    label: "AI Agent",
    color: "#B200FF",
    desc: "Qualify & engage 24/7",
  },
  {
    id: "crm",
    label: "CRM",
    color: "#FF007F",
    desc: "Unified intelligence",
  },
  {
    id: "auto",
    label: "Automation",
    color: "#FF3F8A",
    desc: "Workflows on autopilot",
  },
  {
    id: "cust",
    label: "Customer",
    color: "#FF6000",
    desc: "Activated, delighted",
  },
  {
    id: "rev",
    label: "Revenue",
    color: "#FFB400",
    desc: "Recurring growth",
  },
];

const formatMoney = (value) => {
  const number = Number(value) || 0;

  return number.toLocaleString("en-IN", {
    maximumFractionDigits: 0,
  });
};

const formatShortMoney = (value) => {
  const number = Number(value) || 0;

  if (number >= 10000000) {
    return `₹${(number / 10000000).toFixed(1)}Cr`;
  }

  if (number >= 100000) {
    return `₹${(number / 100000).toFixed(1)}L`;
  }

  if (number >= 1000) {
    return `₹${(number / 1000).toFixed(1)}K`;
  }

  return `₹${Math.round(number)}`;
};

function InputCard({
  label,
  value,
  setValue,
  prefix,
  suffix,
  description,
}) {
  return (
    <div className="glass rounded-2xl p-5 border border-white/10">
      <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-300/70">
        {label}
      </div>

      <div className="mt-1 text-[11px] text-white/40">
        {description}
      </div>

      <div className="mt-3 flex items-center rounded-xl bg-white/[0.04] border border-white/10 overflow-hidden">
        {prefix && (
          <span className="px-3 text-white/50 text-sm">
            {prefix}
          </span>
        )}

        <input
          type="number"
          min="0"
          value={value}
          onChange={(e) => setValue(Number(e.target.value))}
          className="w-full min-w-0 bg-transparent px-3 py-3 text-white text-lg font-semibold outline-none"
        />

        {suffix && (
          <span className="px-3 text-white/50 text-sm">
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
}

function StatCard({
  title,
  value,
  subtitle,
  icon,
}) {
  return (
    <div className="glass rounded-2xl p-6 border border-white/10 hover:border-cyan-400/30 transition-all">
      <div className="flex items-center justify-between">
        <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-300/70">
          {title}
        </div>

        <span className="text-lg">
          {icon}
        </span>
      </div>

      <div className="mt-3 text-3xl font-[Outfit] font-bold text-white">
        {value}
      </div>

      <div className="mt-2 text-xs text-white/45">
        {subtitle}
      </div>
    </div>
  );
}

function ScenarioCard({
  title,
  description,
  revenue,
  customers,
  roi,
  color,
  recommended,
}) {
  return (
    <div
      className="relative rounded-3xl p-6 bg-white/[0.025] border"
      style={{
        borderColor: `${color}44`,
        boxShadow: `0 0 30px ${color}12`,
      }}
    >
      {recommended && (
        <div
          className="absolute -top-3 right-5 rounded-full px-3 py-1 text-[10px] font-mono uppercase tracking-widest"
          style={{
            color: color,
            background: `${color}18`,
            border: `1px solid ${color}55`,
          }}
        >
          Recommended
        </div>
      )}

      <div className="flex items-center gap-3">
        <div
          className="w-3 h-3 rounded-full"
          style={{
            background: color,
            boxShadow: `0 0 14px ${color}`,
          }}
        />

        <h3 className="text-xl font-bold text-white">
          {title}
        </h3>
      </div>

      <p className="mt-2 text-sm text-white/45">
        {description}
      </p>

      <div className="mt-6">
        <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
          Annual Revenue
        </div>

        <div className="mt-2 text-3xl font-bold text-white">
          {formatShortMoney(revenue)}
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-white/[0.04] p-3">
          <div className="text-[10px] uppercase text-white/40">
            Customers
          </div>

          <div className="mt-1 font-semibold text-white">
            {Math.round(customers).toLocaleString("en-IN")}
          </div>
        </div>

        <div className="rounded-xl bg-white/[0.04] p-3">
          <div className="text-[10px] uppercase text-white/40">
            ROI
          </div>

          <div className="mt-1 font-semibold text-white">
            {roi.toFixed(1)}%
          </div>
        </div>
      </div>
    </div>
  );
}

export default function RevenueEngine() {
  const [monthlyLeads, setMonthlyLeads] = useState(1000);
  const [conversionRate, setConversionRate] = useState(8);
  const [customerValue, setCustomerValue] = useState(15000);
  const [monthlyGrowth, setMonthlyGrowth] = useState(5);
  const [monthlyInvestment, setMonthlyInvestment] = useState(100000);

  const projection = useMemo(() => {
    const data = [];

    let leads = Number(monthlyLeads) || 0;

    for (let month = 1; month <= 12; month += 1) {
      const customers =
        leads * ((Number(conversionRate) || 0) / 100);

      const revenue =
        customers * (Number(customerValue) || 0);

      const investment =
        Number(monthlyInvestment) || 0;

      const profit = revenue - investment;

      data.push({
        month: month,
        leads: leads,
        customers: customers,
        revenue: revenue,
        investment: investment,
        profit: profit,
      });

      leads =
        leads *
        (1 + ((Number(monthlyGrowth) || 0) / 100));
    }

    let year1Revenue = 0;
    let year1Investment = 0;
    let totalCustomers = 0;

    data.forEach((item) => {
      year1Revenue += item.revenue;
      year1Investment += item.investment;
      totalCustomers += item.customers;
    });

    const year1Profit =
      year1Revenue - year1Investment;

    let roi = 0;

    if (year1Investment > 0) {
      roi =
        (year1Profit / year1Investment) * 100;
    }

    const averageMonthlyRevenue =
      year1Revenue / 12;

    let breakEvenMonth = 0;

    for (let i = 0; i < data.length; i += 1) {
      if (data[i].revenue >= data[i].investment) {
        breakEvenMonth = data[i].month;
        break;
      }
    }

    const growthFactor =
      1 + ((Number(monthlyGrowth) || 0) / 100);

    const year2Revenue =
      year1Revenue *
      Math.pow(growthFactor, 12);

    const year3Revenue =
      year2Revenue *
      Math.pow(growthFactor, 12);

    return {
      data: data,
      year1Revenue: year1Revenue,
      year1Investment: year1Investment,
      year1Profit: year1Profit,
      totalCustomers: totalCustomers,
      roi: roi,
      averageMonthlyRevenue: averageMonthlyRevenue,
      breakEvenMonth: breakEvenMonth,
      year2Revenue: year2Revenue,
      year3Revenue: year3Revenue,
    };
  }, [
    monthlyLeads,
    conversionRate,
    customerValue,
    monthlyGrowth,
    monthlyInvestment,
  ]);

  const scenarios = useMemo(() => {
    const calculate = (
      leadMultiplier,
      conversionMultiplier,
      growthMultiplier
    ) => {
      let leads =
        (Number(monthlyLeads) || 0) *
        leadMultiplier;

      let revenue = 0;
      let customers = 0;

      for (let month = 1; month <= 12; month += 1) {
        const converted =
          leads *
          ((Number(conversionRate) || 0) / 100) *
          conversionMultiplier;

        const monthlyRevenue =
          converted *
          (Number(customerValue) || 0);

        revenue += monthlyRevenue;
        customers += converted;

        leads =
          leads *
          (
            1 +
            (
              ((Number(monthlyGrowth) || 0) *
                growthMultiplier) /
              100
            )
          );
      }

      const investment =
        (Number(monthlyInvestment) || 0) * 12;

      let roi = 0;

      if (investment > 0) {
        roi =
          ((revenue - investment) / investment) *
          100;
      }

      return {
        revenue: revenue,
        customers: customers,
        roi: roi,
      };
    };

    return {
      conservative: calculate(0.8, 0.85, 0.7),
      expected: calculate(1, 1, 1),
      aggressive: calculate(1.25, 1.15, 1.35),
    };
  }, [
    monthlyLeads,
    conversionRate,
    customerValue,
    monthlyGrowth,
    monthlyInvestment,
  ]);

  let maxRevenue = 1;

  projection.data.forEach((item) => {
    if (item.revenue > maxRevenue) {
      maxRevenue = item.revenue;
    }
  });

  return (
    <section
      id="revenue-engine"
      className="relative py-28 px-6"
      data-testid="revenue-engine-section"
    >
      <div className="max-w-7xl mx-auto">

        {/* HEADER */}
        <div className="text-center mb-14">

          <p className="font-mono text-[11px] tracking-[0.4em] text-cyan-300/80 mb-3">
            ◆ MUNNEXA REVENUE INTELLIGENCE
          </p>

          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-[Outfit] font-bold">
            Build your{" "}
            <span className="text-gradient">
              revenue projection.
            </span>
          </h2>

          <p className="mt-4 text-white/65 max-w-2xl mx-auto">
            Model your leads, conversion rate, customer value and
            investment to understand your potential revenue growth.
          </p>

        </div>

        {/* PIPELINE */}
        <div className="relative overflow-x-auto pb-4">

          <div className="min-w-[860px] flex items-center justify-between gap-2 px-4 py-10 glass rounded-3xl holo-border relative">

            <div className="absolute left-6 right-6 top-1/2 h-1.5 rounded-full tube overflow-hidden">
              <div className="absolute inset-0 shimmer rounded-full" />
            </div>

            {STAGES.map((stage, index) => (
              <div
                key={stage.id}
                className="relative z-10 flex flex-col items-center min-w-[100px]"
                data-testid={`pipeline-${stage.id}`}
              >
                <div
                  className="w-16 h-16 rounded-full grid place-items-center ring-2 ring-white/10"
                  style={{
                    background:
                      `radial-gradient(circle at 30% 30%, ${stage.color}55, ${stage.color}cc 60%, transparent 80%)`,
                    boxShadow:
                      `0 0 28px ${stage.color}aa, inset 0 0 18px ${stage.color}66`,
                  }}
                >
                  <span className="font-[Outfit] font-bold text-white">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </div>

                <div className="mt-3 text-white font-[Outfit] font-semibold text-sm">
                  {stage.label}
                </div>

                <div className="font-mono text-[10px] uppercase tracking-wider text-white/45 mt-1 text-center">
                  {stage.desc}
                </div>
              </div>
            ))}

          </div>

        </div>

        {/* INPUT SECTION */}
        <div className="mt-12">

          <div className="mb-5">

            <p className="font-mono text-[10px] uppercase tracking-widest text-cyan-300/70">
              PROJECTION CONTROLS
            </p>

            <h3 className="mt-2 text-2xl font-bold text-white">
              Configure your business model
            </h3>

            <p className="mt-1 text-sm text-white/45">
              Change any value below and the forecast updates instantly.
            </p>

          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">

            <InputCard
              label="Monthly Leads"
              value={monthlyLeads}
              setValue={setMonthlyLeads}
              description="Qualified opportunities"
            />

            <InputCard
              label="Conversion Rate"
              value={conversionRate}
              setValue={setConversionRate}
              suffix="%"
              description="Lead to customer"
            />

            <InputCard
              label="Customer Value"
              value={customerValue}
              setValue={setCustomerValue}
              prefix="₹"
              description="Average revenue/customer"
            />

            <InputCard
              label="Monthly Growth"
              value={monthlyGrowth}
              setValue={setMonthlyGrowth}
              suffix="%"
              description="Monthly lead growth"
            />

            <InputCard
              label="Monthly Investment"
              value={monthlyInvestment}
              setValue={setMonthlyInvestment}
              prefix="₹"
              description="Marketing + operations"
            />

          </div>

        </div>

        {/* KPI CARDS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-8">

          <StatCard
            title="Year 1 Revenue"
            value={formatShortMoney(projection.year1Revenue)}
            subtitle={`₹${formatMoney(projection.year1Revenue)} projected`}
            icon="💰"
          />

          <StatCard
            title="Customers"
            value={Math.round(
              projection.totalCustomers
            ).toLocaleString("en-IN")}
            subtitle="Projected Year 1"
            icon="👥"
          />

          <StatCard
            title="Monthly Average"
            value={formatShortMoney(
              projection.averageMonthlyRevenue
            )}
            subtitle="Average revenue"
            icon="📈"
          />

          <StatCard
            title="Year 1 ROI"
            value={`${projection.roi.toFixed(1)}%`}
            subtitle="Return on investment"
            icon="⚡"
          />

          <StatCard
            title="Break-even"
            value={
              projection.breakEvenMonth > 0
                ? `Month ${projection.breakEvenMonth}`
                : "Not reached"
            }
            subtitle="Revenue covers investment"
            icon="🎯"
          />

        </div>

        {/* MONTHLY FORECAST */}
        <div className="mt-10 glass rounded-3xl p-6 sm:p-8 border border-white/10">

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">

            <div>

              <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-300/70">
                12-MONTH FORECAST
              </div>

              <h3 className="mt-2 text-2xl font-bold text-white">
                Revenue growth trajectory
              </h3>

            </div>

            <div className="rounded-2xl bg-cyan-400/10 border border-cyan-300/20 px-5 py-3">

              <div className="text-[10px] uppercase tracking-widest text-cyan-300/60">
                Year 1 Projection
              </div>

              <div className="mt-1 text-xl font-bold text-cyan-200">
                {formatShortMoney(
                  projection.year1Revenue
                )}
              </div>

            </div>

          </div>

          <div className="mt-8 space-y-5">

            {projection.data.map((item) => {

              const width =
                Math.max(
                  4,
                  (item.revenue / maxRevenue) * 100
                );

              return (
                <div key={item.month}>

                  <div className="flex items-center justify-between mb-2">

                    <div className="flex items-center gap-3">

                      <span className="font-mono text-[10px] text-white/40 w-16">
                        MONTH {item.month}
                      </span>

                      <span className="text-xs text-white/40">
                        {Math.round(
                          item.customers
                        ).toLocaleString("en-IN")}{" "}
                        customers
                      </span>

                    </div>

                    <span className="text-sm font-semibold text-white">
                      ₹{formatMoney(item.revenue)}
                    </span>

                  </div>

                  <div className="h-3 rounded-full bg-white/[0.05] overflow-hidden">

                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${width}%`,
                        background:
                          "linear-gradient(90deg,#00F0FF,#B200FF,#FF007F)",
                        boxShadow:
                          "0 0 18px rgba(0,240,255,.35)",
                      }}
                    />

                  </div>

                </div>
              );
            })}

          </div>

        </div>

        {/* YEAR 1 / 2 / 3 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-8">

          <div className="glass rounded-3xl p-6 border border-cyan-400/20">

            <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-300/70">
              YEAR 01
            </div>

            <div className="mt-3 text-3xl font-bold text-white">
              {formatShortMoney(
                projection.year1Revenue
              )}
            </div>

            <p className="mt-2 text-sm text-white/45">
              Initial growth projection
            </p>

          </div>

          <div className="glass rounded-3xl p-6 border border-purple-400/20">

            <div className="font-mono text-[10px] uppercase tracking-widest text-purple-300/70">
              YEAR 02
            </div>

            <div className="mt-3 text-3xl font-bold text-white">
              {formatShortMoney(
                projection.year2Revenue
              )}
            </div>

            <p className="mt-2 text-sm text-white/45">
              Compounded growth projection
            </p>

          </div>

          <div className="glass rounded-3xl p-6 border border-pink-400/20">

            <div className="font-mono text-[10px] uppercase tracking-widest text-pink-300/70">
              YEAR 03
            </div>

            <div className="mt-3 text-3xl font-bold text-white">
              {formatShortMoney(
                projection.year3Revenue
              )}
            </div>

            <p className="mt-2 text-sm text-white/45">
              Long-term scale projection
            </p>

          </div>

        </div>

        {/* SCENARIOS */}
        <div className="mt-12">

          <div className="text-center mb-7">

            <p className="font-mono text-[10px] uppercase tracking-widest text-cyan-300/70">
              NEXA FORECAST MODEL
            </p>

            <h3 className="mt-2 text-3xl font-bold text-white">
              Three possible growth paths
            </h3>

            <p className="mt-2 text-sm text-white/45">
              Compare conservative, expected and aggressive outcomes.
            </p>

          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

            <ScenarioCard
              title="Conservative"
              description="Lower acquisition and slower growth."
              revenue={scenarios.conservative.revenue}
              customers={scenarios.conservative.customers}
              roi={scenarios.conservative.roi}
              color="#7DD3FC"
            />

            <ScenarioCard
              title="Expected"
              description="Balanced projection based on your assumptions."
              revenue={scenarios.expected.revenue}
              customers={scenarios.expected.customers}
              roi={scenarios.expected.roi}
              color="#00F0FF"
              recommended={true}
            />

            <ScenarioCard
              title="Aggressive"
              description="Higher acquisition and accelerated growth."
              revenue={scenarios.aggressive.revenue}
              customers={scenarios.aggressive.customers}
              roi={scenarios.aggressive.roi}
              color="#FF007F"
            />

          </div>

        </div>

        {/* EXECUTIVE INSIGHT */}
        <div className="mt-10 rounded-3xl p-7 sm:p-9 bg-gradient-to-br from-fuchsia-500/10 via-purple-500/10 to-cyan-500/10 border border-fuchsia-400/20">

          <div className="flex items-start gap-4">

            <div className="w-12 h-12 rounded-2xl bg-fuchsia-500/10 border border-fuchsia-300/20 grid place-items-center text-xl">
              🎯
            </div>

            <div>

              <div className="font-mono text-[10px] uppercase tracking-widest text-fuchsia-300">
                NEXA EXECUTIVE INSIGHT
              </div>

              <h3 className="mt-1 text-2xl font-bold text-white">
                Your revenue opportunity
              </h3>

            </div>

          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">

            <div className="rounded-2xl bg-black/20 p-5">

              <div className="text-xs text-white/40">
                PROJECTED REVENUE
              </div>

              <div className="mt-2 text-xl font-bold text-white">
                ₹{formatMoney(
                  projection.year1Revenue
                )}
              </div>

            </div>

            <div className="rounded-2xl bg-black/20 p-5">

              <div className="text-xs text-white/40">
                PROJECTED PROFIT
              </div>

              <div className="mt-2 text-xl font-bold text-emerald-300">
                ₹{formatMoney(
                  projection.year1Profit
                )}
              </div>

            </div>

            <div className="rounded-2xl bg-black/20 p-5">

              <div className="text-xs text-white/40">
                PROJECTED ROI
              </div>

              <div className="mt-2 text-xl font-bold text-cyan-300">
                {projection.roi.toFixed(1)}%
              </div>

            </div>

          </div>

          <div className="mt-6 text-sm leading-7 text-white/65">

            Based on your current assumptions, the model
            projects approximately{" "}

            <strong className="text-white">
              ₹{formatMoney(
                projection.year1Revenue
              )}
            </strong>{" "}

            in Year 1 revenue.

          </div>

        </div>

        {/* DISCLAIMER */}
        <div className="mt-6 text-center">

          <p className="text-[11px] text-white/30 max-w-3xl mx-auto">
            Projection figures are estimates based on the
            assumptions entered above. Actual revenue,
            conversion, customer acquisition and ROI may vary
            according to market conditions and business
            performance.
          </p>

        </div>

      </div>
    </section>
  );
}
